/**
 * InsertSection.tsx - Insert Options for Estimation.pk Quote Form
 * 
 * Ported directly from HPP Building's StepperForm.tsx (lines 734-850)
 * Uses the SAME material/GSM/printing system as the main box.
 */

import React from 'react';
import { Package, ChevronDown, ChevronUp, Layers } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { InsertSpecification, Material, CorrugatedType, PrintingColors } from '../../../types';

// GSM options by material type (same as box)
const GSM_OPTIONS: Record<Material, number[]> = {
  cardboard: [210, 230, 250, 270, 300, 350, 400, 420],
  kraft: [210, 230, 250, 270, 300, 350, 400],
  corrugated: [], // Uses corrugatedType instead
  rigid: [350, 400, 450, 500, 550],
};

const CORRUGATED_OPTIONS: { value: CorrugatedType; label: string }[] = [
  { value: 'single-kraft', label: 'Single Kraft' },
  { value: 'single-cardboard', label: 'Single Cardboard' },
  { value: 'double-kraft', label: 'Double Kraft' },
  { value: 'double-cardboard', label: 'Double Cardboard' },
];

// Default insert specification
export const getDefaultInsertSpec = (): InsertSpecification => ({
  enabled: false,
  openLength: 0,
  openWidth: 0,
  material: 'cardboard',
  gsm: 300,
  printing: { cmyk: 0, pantone: 0, metallic: 0 },
  insidePrinting: { cmyk: 0, pantone: 0, metallic: 0 },
  laminationType: null,
  laminationBothSides: false,
});

interface InsertSectionProps {
  expanded: boolean;
  onToggle: () => void;
  insertSpec: InsertSpecification;
  onInsertChange: (spec: InsertSpecification) => void;
  boxQuantity: number;
}

export const InsertSection = React.memo(function InsertSection({
  expanded,
  onToggle,
  insertSpec,
  onInsertChange,
  boxQuantity,
}: InsertSectionProps) {
  const isEnabled = insertSpec.enabled;

  const handleToggle = (enabled: boolean) => {
    onInsertChange({
      ...insertSpec,
      enabled,
    });
  };

  const handleFieldChange = (field: keyof InsertSpecification, value: unknown) => {
    onInsertChange({
      ...insertSpec,
      [field]: value,
    });
  };

  const handleMaterialChange = (material: Material) => {
    const newSpec: InsertSpecification = {
      ...insertSpec,
      material,
      // Reset GSM to default for new material
      gsm: material === 'corrugated' ? 0 : (GSM_OPTIONS[material]?.[3] || 300),
      corrugatedType: material === 'corrugated' ? 'single-kraft' : undefined,
    };
    onInsertChange(newSpec);
  };

  const handlePrintingChange = (
    type: 'cmyk' | 'pantone' | 'metallic',
    value: number,
    isInside: boolean
  ) => {
    const field = isInside ? 'insidePrinting' : 'printing';
    const current = insertSpec[field];
    onInsertChange({
      ...insertSpec,
      [field]: {
        ...current,
        [type]: Math.max(0, Math.min(4, value)),
      },
    });
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-primary" />
          <span className="font-medium text-foreground">Insert (Optional)</span>
          {isEnabled && (
            <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
              {insertSpec.material === 'corrugated' 
                ? insertSpec.corrugatedType?.replace('-', ' ')
                : `${insertSpec.material} ${insertSpec.gsm}gsm`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Enable/Disable Toggle */}
          <label 
            className="relative inline-flex items-center cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => handleToggle(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-muted rounded-full peer peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
          </label>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && isEnabled && (
        <div className="px-4 pb-4 space-y-4 border-t border-border pt-4">
          {/* Dimensions */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Open Length (in)
              </label>
              <input
                type="number"
                value={insertSpec.openLength || ''}
                onChange={(e) => handleFieldChange('openLength', parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                step="0.01"
                min="0"
                className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Open Width (in)
              </label>
              <input
                type="number"
                value={insertSpec.openWidth || ''}
                onChange={(e) => handleFieldChange('openWidth', parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                step="0.01"
                min="0"
                className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          {/* Material & GSM */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Material
              </label>
              <select
                value={insertSpec.material}
                onChange={(e) => handleMaterialChange(e.target.value as Material)}
                className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="cardboard">Cardboard</option>
                <option value="corrugated">Corrugated</option>
                <option value="kraft">Kraft</option>
              </select>
            </div>
            <div>
              {insertSpec.material === 'corrugated' ? (
                <>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    Construction
                  </label>
                  <select
                    value={insertSpec.corrugatedType || 'single-kraft'}
                    onChange={(e) => handleFieldChange('corrugatedType', e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    {CORRUGATED_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">
                    GSM
                  </label>
                  <select
                    value={insertSpec.gsm}
                    onChange={(e) => handleFieldChange('gsm', parseInt(e.target.value))}
                    className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    {GSM_OPTIONS[insertSpec.material]?.map((gsm) => (
                      <option key={gsm} value={gsm}>
                        {gsm} GSM
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
          </div>

          {/* Quantity Override */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Quantity (optional, defaults to {boxQuantity})
            </label>
            <input
              type="number"
              value={insertSpec.quantityOverride || ''}
              onChange={(e) => {
                const val = e.target.value === '' ? undefined : parseInt(e.target.value);
                handleFieldChange('quantityOverride', val);
              }}
              placeholder={`${boxQuantity} (same as box)`}
              min="1"
              className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {/* Printing Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Insert Printing</span>
            </div>

            {/* Outside Printing */}
            <div className="space-y-2">
              <span className="text-xs text-muted-foreground">Outside</span>
              <div className="grid grid-cols-3 gap-2">
                {(['cmyk', 'pantone', 'metallic'] as const).map((type) => (
                  <div key={type}>
                    <label className="block text-xs text-muted-foreground mb-1 capitalize">
                      {type}
                    </label>
                    <select
                      value={insertSpec.printing[type]}
                      onChange={(e) => handlePrintingChange(type, parseInt(e.target.value), false)}
                      className="w-full px-2 py-1.5 bg-background border border-input rounded text-sm"
                    >
                      {[0, 1, 2, 3, 4].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {/* Inside Printing */}
            <div className="space-y-2">
              <span className="text-xs text-muted-foreground">Inside</span>
              <div className="grid grid-cols-3 gap-2">
                {(['cmyk', 'pantone', 'metallic'] as const).map((type) => (
                  <div key={type}>
                    <label className="block text-xs text-muted-foreground mb-1 capitalize">
                      {type}
                    </label>
                    <select
                      value={insertSpec.insidePrinting[type]}
                      onChange={(e) => handlePrintingChange(type, parseInt(e.target.value), true)}
                      className="w-full px-2 py-1.5 bg-background border border-input rounded text-sm"
                    >
                      {[0, 1, 2, 3, 4].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Lamination */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-muted-foreground">
              Lamination
            </label>
            <div className="flex flex-wrap gap-2">
              {(['none', 'gloss', 'matte', 'soft-touch'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleFieldChange('laminationType', type === 'none' ? null : type)}
                  className={cn(
                    'px-3 py-1.5 text-xs rounded-lg border transition-colors',
                    (insertSpec.laminationType === type || (type === 'none' && !insertSpec.laminationType))
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-input hover:bg-muted'
                  )}
                >
                  {type === 'none' ? 'None' : type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            {insertSpec.laminationType && (
              <label className="flex items-center gap-2 text-sm mt-2">
                <input
                  type="checkbox"
                  checked={insertSpec.laminationBothSides || false}
                  onChange={(e) => handleFieldChange('laminationBothSides', e.target.checked)}
                  className="w-4 h-4 rounded border-input"
                />
                <span className="text-muted-foreground">Apply to both sides</span>
              </label>
            )}
          </div>
        </div>
      )}

      {/* Collapsed hint when enabled but collapsed */}
      {!expanded && isEnabled && (
        <div className="px-4 pb-3 text-xs text-muted-foreground">
          {insertSpec.openLength}&quot; × {insertSpec.openWidth}&quot; • Click to expand
        </div>
      )}
    </div>
  );
});

export default InsertSection;
